import { IInfoProvidingControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { IControlInfoObject } from "UI/UI/UI/XGControls/ControlInfo";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { Control } from "../../../UI.BaseControls/Control";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { IWidgetResizeEvent } from "../../../UI.BaseControls/IEvent";
import { ElementUtils } from "../../../UI.ElementarControls/Helpers/ElementUtils";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Column } from "../Columns/Column";
import { IHeaderTemplate } from "./ITemplate";

export class HeaderTemplate extends Control implements IHeaderTemplate, IInfoProvidingControl {
    //#region Events
    public headerClickEvent: CustomEvent;
    //#endregion Events

    //#region Listeners
    //#endregion Listeners

    //#region Fields
    //public Element: HTMLTableHeaderCellElement;
    private _spanTextElement: HTMLSpanElement;
    private _spanOrderElement: HTMLSpanElement; // element pro zobrazeni ikonky sipky, pro smer trideni sloupce
    private _spanFilterElement: HTMLSpanElement; // element pro zobrazeni ikonky pro filtraci
    public dropDownButtonElement: HTMLButtonElement; // element pro zorbazeni tlacitka na dropdownmenu
    private _divFilterRow: HTMLElement; // element pro zobrazeni aplikovanych filtru na sloupec
    private _sortableDraggableElement: HTMLElement; // element, pomocí kterého se řeší řazení sloupečku
    public index: number;
    private _column: Column;
    private _isResizing: boolean = false;
    public isCompact: boolean = false;

    //#endregion Fields

    //#region Properties
    public get parentColumn(): Column {
        return this._column;
    }

    public set parentColumn(value: Column) {
        this._column = value;

        if (this._column) {
            this.setAttribute("data-attrname", this._column.attrName);

            // je to zde z duvodu toho, ze ve Firefoxu jsou CustomElementy reseny polyfillem a ne nativne
            // a tedy dochazelo k pozdejsimu nacteni lokalizaci z atriburu nez vyovlani teto metody
            this._column.registerEventListener("attributechange", (e: CustomEvent) => {
                const { attrName, newVal } = e.detail;
                if (attrName === "data-lockey" || attrName === "data-locparams") {
                    const locKey = attrName === "data-lockey" ? newVal : this._column.locKey;
                    const locParams = attrName === "data-locparams" ? newVal : this._column.locParams;
                    this._updateLocKey(locKey, locParams);
                }
            });
            this._updateLocKey(this._column.locKey, this._column.locParams);

            if (this._column.withoutAttribute || this._column.hideColumnMenu) { // U tohoto typu sloupce menu neobsahuje nic užitečného, proto ho odstraníme
                this.dropDownButtonElement.remove();
            }
        }
    }
    //#endregion Properties

    //#region Constructors
    //#endregion Constructors

    //#region Methods
    public init(): void {
        // Prazdna metoda
    }

    public render(column: Column, index: number): HTMLElement {
        this.index = index;
        this.parentColumn = column;

        const id: string = "sl" + this.index;

        this.setAttribute("data-id", id);
        this.setAttribute("data-index", id);

        this.style.minWidth = "20px";
        this.style.width = `${column.width}px`;

        if (this.isCompact) { // u kompaktního seznamu nechceme nabídku pro filtrování
            const els = this.querySelectorAll(".DropDownMenu_item.icon.ui-more-vertical.btn.flat, span.FilterElement");
            for (const el of els) {
                el.remove();
            }
        }

        return this;
    }

    /** Vrací objekt, který obsahuje informace o prvku */
    public async getControlInfo(childInfo: Partial<IControlInfoObject>): Promise<Partial<IControlInfoObject>> {
        const dataAttributes: any = {};
        Object.assign(dataAttributes, this.dataset);
        return {
            ...childInfo,
            attributeName: this.dataset.attrname,
            locKey: this._column.locKey,
            additionalInfo: {
                dataAttributes: { ...dataAttributes },
                implementationClass: this.constructor.name,
            },
        };
    }

    //#endregion Methods

    //#region HTMLElement
    protected override _constructor(): void {
        super._constructor();

        const divTitleRow = document.createElement("div");
        divTitleRow.classList.add("header-title");

        this._spanOrderElement = document.createElement("span");
        this._spanTextElement = document.createElement("span");
        this.dropDownButtonElement = document.createElement("button");
        this._spanOrderElement.classList.add("OrderElement");
        this.dropDownButtonElement.classList.add("DropDownMenu_item", "icon", "ui-more-vertical", "btn", "flat");
        Localization.addLocDescData(this.dropDownButtonElement, "GUI-668905", "title");
        this._sortableDraggableElement = createElement({ classes: ["sort-drag-handle", "column-sortable"] });

        this._spanFilterElement = createElement({ type: "span", classes: "FilterElement" });

        this.widgetResizable(true);

        divTitleRow.appendChild(this._sortableDraggableElement);
        divTitleRow.appendChild(this._spanTextElement);
        divTitleRow.appendChild(this._spanOrderElement);
        divTitleRow.appendChild(this._spanFilterElement);
        divTitleRow.appendChild(this.dropDownButtonElement);

        this._divFilterRow = document.createElement("div");
        this._divFilterRow.classList.add("header-filter");

        this.appendChild(divTitleRow);
        this.appendChild(this._divFilterRow);

        //// Presunuto ze sloupcu sem, protoze pri sortable doslo k odpojeni vsech udalosti, je treba je znova zaregistrovat pri kazdem attach
        this.registerEventListener("HeaderClickEvent", (evt: CustomEvent) => {
            ElementUtils.callEvent(this.parentColumn, "HeaderClickEvent", { "state": evt.detail.state, "shiftKey": evt.detail.shiftKey });
        });
        this.registerEventListener("dropdownmenushow", (e: CustomEvent) => {
            this._callDropdownMenuShow(this.parentColumn, { column: this, dropdownelemnt: e.detail.element, originalEvent: e.detail.originalEvent });
        });

        //this.registerEventListener("mousemove", (e: MouseEvent) => this.mouseMove(e));
        this.registerEventListener("click", (e: MouseEvent) => {
            this._mouseClick(e);
        });

        this._eventsController.addListener(this.dropDownButtonElement, "click", (e: PointerEvent) => {
            EventHelper.stopPropagation(e);
            this._callDropdownMenuShow(this, { element: this.dropDownButtonElement, template: this, originalEvent: e });
        });

        this._eventsController.addListener(this.dropDownButtonElement, "focus", (_e: MouseEvent) => {
            this.callEvent("focus");
        });

        this.registerEventListener("resizabledrag", (e: IWidgetResizeEvent) => {
            this._isResizing = true;
            this.parentColumn.width = e.detail.width;
            ElementUtils.callEvent(this.parentColumn, "resizabledrag", e.detail);
        });
        this.addEventListener("mousedown", (_e: MouseEvent) => {
            if (this._isResizing) {
                this._isResizing = false;
            }
            // JQuery UI widget sortable žere event mousedown, takže focusnutý element focus neztratí
            // a tím se nemusí proběhnout navázané události (např. ValueChanged).
            // Toto se tedy stará o ztrátu focusu
            const activeElement = document.activeElement;
            if (activeElement && activeElement instanceof HTMLElement)
                activeElement.blur();
        });

        this._divFilterRow.addEventListener("click", (e: MouseEvent) => {
            EventHelper.stopPropagation(e);
            this._callDropdownMenuShow(this, { element: this.dropDownButtonElement, template: this });
        });

        this.disposeOnDetach = false;
    }

    private _callDropdownMenuShow(element: HTMLElement, detail: any): void {
        ElementUtils.callEvent(element, "dropdownmenushow", detail);
    }

    protected override connectedCallback(): void {
        super.connectedCallback();

        if (this.parentColumn)
            this.style.width = `${this.parentColumn.width}px`;
    }

    /**
     * Zakáže možnost řazení kliknutím na hlavičku sloupce
     */
    public disableSort(): void {
        if (this._sortableDraggableElement)
            this._sortableDraggableElement.classList.remove("column-sortable");
    }

    public setFilter(filter: HTMLElement = null): void {
        this._divFilterRow.innerHTML = "";

        if (filter) {
            this._divFilterRow.appendChild(filter);
        }
    }

    private _updateLocKey(locKey: string, locParams?: string[]): void {
        if (locKey) {
            Localization.addLocTxtData(this._spanTextElement, locKey, undefined, locParams);
            Localization.addLocDescData(this, locKey, "title", locParams);
        } else {
            this._spanTextElement.innerHTML = "&nbsp;";
        }
    }

    private _mouseClick(e: MouseEvent): void {
        if (this._isResizing) {
            this._isResizing = false;
            return;
        }
        if (e.ctrlKey || e.metaKey) // Zobrazení informací o komponentě
            return;

        const order_value = parseInt(this.getAttribute("order")) || 3;

        if (order_value == 3) { // je nesetrideny, tak se setridi vzestupne
            this._dispatchHeaderClickEvent(1, e.shiftKey);
        } else if (order_value == 1) { // je setrideny vzestupne, tak se setridi sestupne
            this._dispatchHeaderClickEvent(2, e.shiftKey);
        } else if (order_value == 2) { // je setrideny sestupne, tak se trideni odebere
            this._dispatchHeaderClickEvent(3, e.shiftKey);
        }
    }

    /**
     * Vyvola novou udalost, ktera se pouziva pri trideni
     * @param state - je nesetrideny, tak se setridi vzestupne(1), je setrideny vzestupne, tak se setridi sestupne (2), je setrideny sestupne, tak se trideni odebere (3)
     * @param shiftKey - true pokud uzivatel drzi shift
     */
    private _dispatchHeaderClickEvent(state: number, shiftKey: boolean): void {
        this.callEvent("HeaderClickEvent", { "state": state, "shiftKey": shiftKey });
    }

    // TODO: DT 23.4.2018 - metoda nevyužita ale prozatím ponechána - proto flag "ts-ignore" níže
    // @ts-ignore
    private _mouseMove(e: MouseEvent): void {
        //var hoveredTh = e.target;
        //logger.log("mouse move");
        const pWidth = this.clientWidth;
        //var pOffset = this.offsetWidth;

        const rect = this.getBoundingClientRect();

        const x = e.pageX - rect.left;

        if (pWidth - 5 > x) //klik na klasickou hlavicku => trideni
            this.style.cursor = "pointer";
        else //klik na pravych 5px => resizovani hlavicky
            this.style.cursor = "col-resize";
    }

    public override dispose(): void {
        super.dispose();
    }

    //#endregion HTMLElement
}

registerCustomElement("tsc-headertemplate", HeaderTemplate);
